import React, { useEffect, useState } from "react";
import GoogleMapReact from "google-map-react";
import { PickupStore } from "../../hooks/usePickupStores";
import markerImage from "./marker.png";

interface GeometryLocation {
  lat: number;
  lng: number;
}

interface Geometry {
  location: GeometryLocation;
}

interface MapProps {
  stores: PickupStore[];
  selected?: PickupStore;
  onSelect?: (id: string) => void;
  styles?: React.CSSProperties;
}

const StyledMap: React.FC<MapProps> = ({ stores, selected, onSelect, styles }) => {
  const [center, setCenter] = useState<GeometryLocation | undefined>();
  const [zoom, setZoom] = useState<number | undefined>();

  useEffect(() => {
    if (selected?.geometry?.location) {
      setZoom(15);
      setCenter(selected.geometry.location);
    } else {
      setZoom(11);
      setCenter({ lat: 48.1613, lng: 10.1136 }); // Fallback: Memmingen
    }
  }, [selected]);

  const renderMarkers = () => {
    if (selected?.geometry?.location) {
      return (
        <Marker
          lat={selected.geometry.location.lat}
          lng={selected.geometry.location.lng}
          name={selected.name}
          id={selected.id}
        />
      );
    }

    return stores.map((store) => {
      if (!store.geometry?.location) return null;
      return (
        <Marker
          key={`marker-${store.id}`}
          lat={store.geometry.location.lat}
          lng={store.geometry.location.lng}
          name={store.name}
          id={store.id}
          onSelect={onSelect}
        />
      );
    });
  };

  return (
    <div style={styles ?? { minHeight: "100vh", height: "100%", width: "100%" }}>
      <GoogleMapReact
        bootstrapURLKeys={{ key: 'AIzaSyAveFSPWTXijCY1hPFvMNyp3r-4EyptMbY' }}
        center={center}
        zoom={zoom}
      >
        {renderMarkers()}
      </GoogleMapReact>
    </div>
  );
};

interface MarkerProps {
  lat: number;
  lng: number;
  name: string;
  id: string;
  onSelect?: (id: string) => void;
}

const Marker: React.FC<MarkerProps> = ({ name, id, onSelect }) => {
  const pinStyle: React.CSSProperties = {
    width: 30,
    height: 30,
    borderRadius: "50% 50% 50% 0",
    background: `url(${markerImage}) no-repeat`,
    backgroundSize: "contain",
    position: "absolute",
    transform: "rotate(-45deg)",
    left: "50%",
    top: "50%",
    margin: "-20px 0 0 -20px",
    animationFillMode: "both",
    animationDuration: "1s",
    cursor: "pointer",
  };

  const pulseContainerStyle: React.CSSProperties = {
    background: "#bc995d",
    borderRadius: "50%",
    height: 14,
    width: 14,
    position: "absolute",
    left: "50%",
    top: "50%",
    margin: "11px 0px 0px -12px",
    transform: "rotateX(55deg)",
    zIndex: -2,
  };

  const afterPulseStyle: React.CSSProperties = {
    content: "''",
    borderRadius: "50%",
    height: 40,
    width: 40,
    position: "absolute",
    margin: "-13px 0 0 -13px",
    animation: "pulsate 1s ease-out infinite",
    opacity: 0,
    boxShadow: "0 0 1px 2px #bc995d",
    animationDelay: "1.1s",
  };

  return (
    <>
      {/* Inject keyframes only once */}
      <style>
        {`
          @keyframes pulsate {
            0% {
              transform: scale(0.1, 0.1);
              opacity: 0.0;
            }
            50% {
              opacity: 1.0;
            }
            100% {
              transform: scale(1.2, 1.2);
              opacity: 0;
            }
          }
          @keyframes bounce {
            0% {
              opacity: 0;
              transform: translateY(-2000px) rotate(-45deg);
            }
            60% {
              opacity: 1;
              transform: translateY(30px) rotate(-45deg);
            }
            80% {
              transform: translateY(-10px) rotate(-45deg);
            }
            100% {
              transform: translateY(0) rotate(-45deg);
            }
          }
        `}
      </style>

      <div title={name} onClick={() => onSelect?.(id)} style={pinStyle} />
      <div style={pulseContainerStyle}>
        <div style={afterPulseStyle} />
      </div>
    </>
  );
};

export default StyledMap;